import moment from 'moment-timezone'
import Vue from 'vue'
import { isEqual } from 'lodash'
import ts from "@mapbox/timespace";

/**
 *
 * @param str
 * @returns {string}
 */
export const toCamelCase = (str = '') => {
  if (!str) {
    return ''
  }

  return str.replace(/\s(.)/g, function ($1) { return $1.toUpperCase() })
    .replace(/\s/g, '')
    .replace(/^(.)/, function ($1) { return $1.toLowerCase() })
}

export const exportToCSV = rows => {
  const csvStr = rows.map(e => e.join(',')).join('\n')
  const content = `data:text/csv;charset=utf-8,${csvStr}`
  const encodedUri = encodeURI(content)
  window.open(encodedUri)
}

export const updateUserParentData = ({ parent, ...user }) => {
  const parentObj = parent && parent.type ? { [`${parent.type}_id`]: parent.id } : {}

  return {
    ...user,
    ...parentObj,
    role_name: user.role,
    sub: Math.floor(Math.random() * 100).toString()
  }
}

export const changedAttributes = (editedItem = {}, original = {}) => {
  let changed = {}
  const editedKeys = Object.keys(editedItem)
  const originalKeys = Object.keys(original)

  originalKeys.filter(key => editedKeys.includes(key)).filter(key => {
    if (editedItem[key] instanceof Array && editedItem[key][0] && editedItem[key][0].hasOwnProperty('id')) {
      return !isEqual(editedItem[key].map(k => k.id), original[key].map(k => k.id))
    }

    if (editedItem[key] instanceof Array) {
      return !isEqual(editedItem[key], original[key].map(k => k.id))
    }

    return editedItem[key] !== original[key]
  }).forEach(key => {
    changed = Object.assign({}, changed, { [key]: editedItem[key] })
  })

  return changed
}

export const equalArrays = (a = [], b = []) => {
  return a.filter(k1 => b.some(k2 => k1 === k2))
}

export const uniqueArray = (array = []) => {
  return [...new Set(array)]
}

export const uniqueObjectsByProperty = (objects = [], property = '') => {
  return [...new Set(objects.map(a => a[property]))]
}

export const limitWords = (str, max, suffix = '...') => {
  if (!str) {
    return str
  }

  return str.length < max ? str : `${str.substr(0, str.substr(0, max - suffix.length).lastIndexOf(' '))}${suffix}`
}

export const metricIsValid = (type, metric) => {
  const validMetrics = {
    time: ['days', 'weeks', 'months', 'years'],
    distance: ['kilometers']
  }

  return validMetrics[type]?.includes(metric) || false
}

export const toMetricValue = (value, type, metric, formatKm = false) => {
  if (!metricIsValid(type, metric)) {
    console.warn(`Invalid metric type defined! ${metric} is not a valid ${type} metric`)
    return value
  }

  if (type === 'time') {
    const toMetric = `as${toTitleCase(metric)}`
    return (moment.duration(value, 'days')[toMetric]()).toFixed(0)
  }

  if (type === 'distance') {
    switch (metric) {
      case 'kilometers':
        if (formatKm) {
          return `${formatDistanceToKilometer(value, 0)} km`
        }
        return formatDistanceToKilometer(value, 0)
    }
  }
}

export const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
}

export const getCurrentVendorType = (parent) => {
  switch (parent.type) {
    case 'enterprise':
      return 'vendor'
    case 'vendor':
      return 'company'
    case 'company':
      return 'department'
  }
}

export function sortByProperty (a, b) {
  if (a.name.toLowerCase() < b.name.toLowerCase()) {
    return -1
  }
  if (a.name.toLowerCase() > b.name.toLowerCase()) {
    return 1
  }
  return 0
}

export const getLocalStorageIdentity = () => {
  let stored = localStorage.getItem('Identity') ||
    localStorage.setItem('Identity', JSON.stringify({}))
  if (!stored) {
    stored = localStorage.setItem('Identity', JSON.stringify({}))
  }
  return stored && JSON.parse(stored)
}

export const server = () => {
  switch (process.env.NODE_ENV) {
    case 'production':
      return 'tcfpapi001'
    case 'development':
      return 'local'
    case 'testing':
      return 'tcftapi001'
    default:
      return 'local'
  }
}

export const log = (data) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log(data)
  }
}

export const getBrowserTime = () => {
  return Intl.DateTimeFormat().resolvedOptions().timezone
}

export function hhmmss (secs) {
  let minutes = Math.floor(secs / 60)
  secs = secs % 60
  const hours = Math.floor(minutes / 60)
  minutes = minutes % 60
  return pad(hours) + ':' + pad(minutes)
}

export function isToday (timestamp) {
  const today = new Date()
  const date = new Date(timestamp)
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
}

export function pad (num) {
  return ('0' + num).slice(-2)
}

export function calendarShortcuts (moment) {
  const lastDayOfMonth = new Date(
    moment(new Date()).subtract(1, 'months').endOf('month').toDate()
  )
  const lastDayOfWeek = new Date(
    moment(new Date()).subtract(1, 'week').endOf('week').toDate()
  )
  const firstDayOfMonth = new Date(
    moment(new Date()).subtract(1, 'months').startOf('month').toDate()
  )
  const firstDayOfWeek = new Date(
    moment(new Date()).subtract(1, 'week').startOf('week').toDate()
  )

  return [
    {
      text: 'last month',
      onClick (self) {
        self.currentValue = [firstDayOfMonth, lastDayOfMonth]
        self.updateDate(true)
      }
    },
    {
      text: 'last week',
      onClick (self) {
        self.currentValue = [firstDayOfWeek, lastDayOfWeek]
        self.updateDate(true)
      }
    }
  ]
}

export const formatDistanceToKilometer = (distance, decimalCount = 2) => {
  return (distance / 1000).toFixed(decimalCount)
}

export const convertUtcDateToPoint = (date, point) => {
  if (!date) {
    return date
  }

  const timestamp = moment.utc(date).valueOf()
  return ts.getFuzzyLocalTimeFromPoint(timestamp, point)
}

export const formatDate = (date, format) => {
  const time = moment.utc(date)
  if (time.format(format) === 'Invalid date') {
    return ''
  }

  return time.local().format(format)
}

export const clientTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

function createLatLng (lat, lng) {
  return {
    lat: parseFloat(lat),
    lng: parseFloat(lng)
  }
}

export function extractTag (id, tagType) {
  let prop = `No ${tagType} specified`
  const entity = this.entities[id]
  if (entity && entity.hasOwnProperty('tags')) {
    if (entity.tags.length &&
      entity.tags.some(tag => tag.type === tagType)) {
      prop = entity.tags.find(tag => tag.type === tagType)
      if (prop) {
        prop = prop.name.en
      }
    }
  }

  return prop
}

export const getCollectionPath = (collection, user) => {
  if (user.role !== 'Super Admin') {
    let rootCollection = collection
    switch (user.parent[0]) {
      case 'enterprise':
        rootCollection = 'enterprises'
        break
      case 'vendor':
        rootCollection = 'vendors'
        break
      case 'company':
        rootCollection = 'companies'
        break
      case 'department':
        rootCollection = 'departments'
        break
    }
    const property_id = `${user.parent_id}`
    if (rootCollection !== collection) {
      return `${rootCollection}/${property_id}/${collection}`
    }
  }

  if (collection === 'entities') {
    return 'adminEntities'
  } else if (collection === 'markers') {
    return 'adminMarkers'
  } else {
    return `${collection}`
  }
}

export const reloadOnNewContent = async (message = '', title = 'Update Available', type = 'warning') => {
  if (localStorage.getItem('update_received') !== '1') {
    await Vue.swal(title, message, 'warning')
    localStorage.setItem('update_received', '1')

    location.reload(true)
  }
}

export const getPaginated = (params, url) => {
  const Q = Object.assign({}, { orderBy: 'created_at', sortedBy: 'desc', limit: 15, page: 1 }, params)

  let query = '?'
  let count = 0
  Object.keys(Q).forEach(key => {
    const value = (key === 'with' && Array.isArray(Q[key])) ? Q[key].join(';') : Q[key]
    if (count === 0) {
      query += `${key}=${value}`
      count++
      return
    }

    query += `&${key}=${value}`
    count++
  })

  return `${url}${query}`
}

export const createApiUrl = (params, url) => {
  const Q = Object.assign({}, params)

  let query = '/'
  let count = 0
  Object.keys(Q).forEach(key => {
    const value = (key === 'with' && Array.isArray(Q[key])) ? Q[key].join(';') : Q[key]
    if (key === 'id') {
      query += `${value}`
      return
    }

    if (count === 0) {
      query += `?${key}=${value}`
      count++
      return
    }

    query += `&${key}=${value}`
    count++
  })

  return `${url}${query}`
}

export function createUserObject ({ ...user }) {
  const Defaults = {
    uuid: null,
    vendor_id: null,
    user_settings: {
      analytics: {
        resultSets: {},
        charts: {}
      }
    }
  }

  const userSettings = Object.assign({}, Defaults.user_settings,
    user.user_settings
  )

  return Object.assign({}, Defaults, {
    uuid: user.id,
    vendor_id: user.vendor_id,
    user_settings: userSettings
  })
}

export function createMarkers (devices) {
  let marker
  let markers = {}
  Object.entries(devices).forEach(
    ([key, device]) => {
      marker = createMarkerFromDevice(device)
      markers = Object.assign({}, markers, marker)
    }
  )

  return markers
}

export function createMarkersFromEntities (entities) {
  let marker
  let markers = {}
  if (entities === typeof 'object') {
    Object.entries(entities).forEach(
      ([key, entity]) => {
        marker = createMarkerFromEntity(entity)
        markers = Object.assign({}, markers, marker)
      }
    )
  } else if (entities === typeof 'array') {
    entities.forEach(entity => {
      marker = createMarkerFromEntity(entity)
      markers = Object.assign({}, markers, marker)
    })
  }
  return markers
}

export function createLatLngObject (latitude, longitude) {
  let lastLatLng
  return new Promise((resolve, reject) => {
    if (latitude === typeof 'float') {
      if (latitude === typeof 'float') {
        lastLatLng = createLatLng(latitude, longitude)
      }
      return lastLatLng
    } else {
      return reject('Latitude can\'t be converted')
    }
  })
}

export function createLatLngForDevice (device) {
  let lastLatLng
  if (device.latitude && device.longitude) {
    lastLatLng = createLatLng(
      device.latitude,
      device.longitude
    )
    if (device.latitude == 0 && device.longitude == 0) {
      return null
    }
  }

  if (device.last_location) {
    lastLatLng = createLatLng(
      device.last_location.latitude,
      device.last_location.longitude
    )
    if (device.last_location.latitude === 0) {
    }
  }

  return lastLatLng
}

export function randomNumber (min, max) {
  return Math.floor(Math.random() * (max - min)) + min
}

export function validateLatLng (latLng) {
  if (latLng) {
    const lat = latLng.latitude || latLng.lat
    const lng = latLng.longitude || latLng.lng

    const pattern = new RegExp('^-?([1-8]?[1-9]|[1-9]0)\\.{1}\\d{1,6}')
    return pattern.test(lat) && pattern.test(lng)
  }
}

export function createMarker (device) {
  return validateLatLng(device) && {
    key: device.id,
    imei: device.imei,
    lastTrip: device.last_trip,
    label: device.title,
    active: true,
    draggable: false,
    position: createLatLngForDevice(device)
  }
}

export function createMarkerFromDevice (device) {
  return validateLatLng(device) && {
    [device.id]: {
      key: device.id,
      imei: device.imei,
      lastTrip: device.last_trip,
      label: device.title,
      active: true,
      draggable: false,
      position: createLatLngForDevice(device)
    }
  }
}

export function sortByKey (array, key) {
  return array.sort((a, b) => {
    const x = a[key]
    const y = b[key]
    return ((x < y) ? -1 : ((x > y) ? 1 : 0))
  })
}

export const queries = (title, range, active = '', vendorId = '', vendorType = '') => {
  let eActive = ''
  let selectedVendor = ''

  if (active) {
    eActive = `and e.active = ${active}`
  }

  if (vendorId && vendorType) {
    selectedVendor = `and e.${vendorType}_id = '${vendorId}'`
  }

  switch (title) {
    case 'Total distance per day':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round((sum(t.distance)/1000), 2) as 'Total distance (km)',
      count(distinct t.id) as 'Total trips per day'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_started_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by Days
      order by Days`

    case 'Total duration per day':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round((sum(t.duration)/3600), 2) as 'Total duration (hours)',
      count(distinct t.id) as 'Total trips per day'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_started_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by Days
      order by Days`

    case 'Average distance per trip':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round(avg(t.distance)/1000,2) as 'Average distance per trip (km)'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_started_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by Days
      order by Days`

    case 'Average duration per trip':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round(avg(t.duration) / 60,2) as 'Average duration per trip (minutes)'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_started_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by Days
      order by Days`

    case 'Average speed per trip':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round(avg(t.average_speed), 2) as 'Average speed per trip (km/h)'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_started_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by Days
      order by Days`

    case 'Average duration per bike per day':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round((sum(t.duration) / 3600) / count(distinct t.device_id), 2) as 'Duration per day per bike (hour)'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      and t.trip_started_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      ${eActive}
      ${selectedVendor}
      ${bike}
      group by Days
      order by Days`

    case 'Average trips per bike per day':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round(avg(t.duration) / 3600,2) as 'Average duration (hour)'
      from entities e, trips t
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      and e.device_id = t.device_id
      ${eActive}
      ${selectedVendor}
      and date(trip_started_at) between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      ${bike} group by Days
      order by Days`

    case 'Average distance per bike per day':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round((sum(t.distance) / 1000) / count(distinct t.device_id), 2) as 'Distance per day per bike (km)' from trips t, entities e where  t.duration between 240 and 14400 and date(t.trip_started_at) between '${range.from} 00:00:00' and '${range.till} 23:59:59' and t.device_id = e.device_id
              ${eActive} ${selectedVendor} ${bike} group by Days order by Days`

    case 'Average speed per bike per day':
      return `select date(t.trip_started_at) as 'Days',
      count(distinct t.device_id) as 'Unique active vehicles',
      round(sum(t.average_speed) / count(distinct t.device_id), 2) as 'Average speed per day per bike (km/h)'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      and t.trip_started_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      ${eActive}
      ${selectedVendor}
      ${bike}
      group by Days
      order by Days`

    case 'New entities per week':
      return `select week(e.created_at) as 'Week Number',
      count(*) as 'Entities'
      from entities e
      where e.created_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      and e.deleted_at is null
      ${eActive}
      ${selectedVendor}
      group by \`Week number\`
      order by \`Week Number\``

    case 'Total distance per month':
      return `select month(t.trip_started_at) as 'Month',
      count(distinct t.device_id) as 'Unique active vehicles',
      round(sum(t.distance)/1000, 2) as 'Total distance (km)',
      count(month(t.trip_ended_at)) as 'Amount of trips'
      from entities e, trips t
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      and e.device_id = t.device_id
      ${eActive}
      ${selectedVendor}
      and date(trip_started_at) between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by Month
      order by Month`

    case 'Total duration per month':
      return `select month(t.trip_ended_at) as 'Month',
      count(distinct t.device_id) as 'Unique active vehicles',
      round(sum(t.duration)/3600, 2) as 'Total duration (hours)',
      count(month(t.trip_ended_at)) as 'Amount of trips'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_ended_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by Month
      order by Month`

    case 'Activity depending on time of the day (UTC time)':
      return `select hour(t.trip_ended_at) as 'hour',
      count(distinct e.device_id) as 'Unique active vehicles',
      count(date(t.trip_ended_at)) as 'Amount of trips',
      round((sum(t.distance)/1000),2) as 'Total distance (km) this hour',
      round(((sum(t.distance)/1000)/count(distinct t.device_id))/(count(*)/count(distinct t.device_id)), 2) as 'Distance per trip (km)',
      round(max(t.distance)/1000, 2) as 'Max distance (km)'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_ended_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by hour
      order by hour`

    case 'Activity depending on day of the week':
      return `select dayname(t.trip_ended_at) as 'day',
      count(distinct t.device_id) as 'Unique active vehicles',
      count(distinct date(t.trip_ended_at)) as 'Amount of days',
      round(count(*)/count(distinct date(t.trip_ended_at)), 1) as 'Average trips per day',
      round((sum(t.distance)/1000)/count(distinct date(t.trip_ended_at)), 2) as 'Average distance this day (km)',
      round(((sum(t.distance)/1000)/count(distinct e.device_id))/(count(*)/count(distinct e.device_id)), 2) as 'Average distance per trip (km)',
      round(max(t.distance)/1000, 2) as 'Max distance (km)'
      from trips t inner join entities e on t.entity_id = e.id
      where t.deleted_at IS NULL
      and t.duration between 240 and 14400
      ${eActive}
      ${selectedVendor}
      and t.trip_ended_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
      group by day
      order by day`

    case 'Average distance per vehicle per day':
      return `SELECT date(trip_ended_at) as 'Date',
        COUNT(DISTINCT (entity_id)) as 'Unique active vehicles',
        ((SUM(distance) / 1000) / COUNT(DISTINCT (entity_id))) AS 'Distance per day per vehicle (km)',
        AVG(average_speed) as 'Average speed (km/h)',
        COUNT(*) AS 'Total trips per day'
        from trips t inner join entities e on t.entity_id = e.id
        where t.deleted_at IS NULL
        and t.duration between 240 and 14400
        ${eActive}
        ${selectedVendor}
        and t.trip_ended_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
        GROUP BY date(t.trip_ended_at)
        ORDER BY date(t.trip_ended_at) ASC;`

    case 'Average duration per vehicle per week':
      return `SELECT
        week(trip_ended_at) as 'Week number',
        count(DISTINCT(entity_id)) as 'Unique active vehicles',
        sum(duration)/3600/count(DISTINCT(entity_id)) as 'Duration per vehicle per week (hours)',
        avg(average_speed) as 'Average speed (km/h)',
        count(*)/count(DISTINCT(entity_id)) as 'Trips per vehicle per week',
        count(*) as 'Total trips per week'
        FROM
        trips t
        LEFT JOIN entities on entities.id=t.entity_id
        WHERE
        t.trip_ended_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
        ${eActive}
        ${selectedVendor}
        GROUP BY
        week(trip_ended_at)
        ORDER BY
        week(trip_ended_at) ASC;`

    case 'Average duration per vehicle per day':
      return `SELECT
        date(trip_ended_at) as 'Date',
        COUNT(DISTINCT (entity_id)) as 'Unique active vehicles',
        ((SUM(duration) / 60) / COUNT(DISTINCT (entity_id))) AS 'Dutation per day per vehicle (minutes)',
        AVG(average_speed) as 'Average speed (km/h)',
        COUNT(*) AS 'Total trips per day'
        from trips t inner join entities e on t.entity_id = e.id
        where t.deleted_at IS NULL
        and t.duration between 240 and 14400
        ${eActive}
        ${selectedVendor}
        and t.trip_ended_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
        GROUP BY date(t.trip_ended_at)
        ORDER BY date(t.trip_ended_at) ASC;`

    case 'Average distance per vehicle per week':
      return `select
        week(trip_ended_at) as 'Week number',
        count(DISTINCT(entity_id)) as 'Unique active vehicles',
        sum(distance)/1000/count(DISTINCT(entity_id)) as 'Distance per vehicle per week (km)',
        avg(average_speed) as 'Average speed (km/h)',
        count(*)/count(DISTINCT(entity_id)) as 'Trips per vehicle per week',
        count(*) as 'Total trips per week'
        from trips t
        left join entities e on t.entity_id = e.id
        where t.deleted_at IS NULL
        and t.duration between 240 and 14400
        ${eActive}
        ${selectedVendor}
        and t.trip_ended_at between '${range.from} 00:00:00' and '${range.till} 23:59:59'
        GROUP BY
        week(trip_ended_at)
        ORDER BY
        week(trip_ended_at) ASC;`
  }
}

export function wasOnline (entity) {
  const device = entity.device
  if (device) {
    return true
  }
  return false
}

export function createMarkerFromEntity (entity, device) {
  if (entity && device) {
    const position = new google.maps.LatLng({
      lat: parseFloat(device.latitude),
      lng: parseFloat(device.longitude)
    })
    return {
      id: entity.device_id,
      imei: entity.device_imei,
      title: entity.name,
      label: entity.short_name,
      entityId: entity.id,
      deviceId: device.id,
      position
    }
  }
}

export const langFilter = (lang) => {
  switch (lang) {
    case 'nl':
    case 'nl-NL':
    case 'nl_NL':
      return 'nl'
    case 'de':
    case 'de-DE':
    case 'de_DE':
      return 'de'
    case 'fr':
    case 'fr-FR':
    case 'fr_FR':
      return 'fr'
    case 'en':
    case 'en-GB':
    case 'en_GB':
    case 'en-US':
    case 'en_US':
      return 'en'
  }
}

export const addZero = (i) => {
  if (i < 10) {
    return `0${i}`
  }

  return i
}

export function syntaxHighlight (json) {
  if (typeof json !== 'string') {
    json = JSON.stringify(json, undefined, 2)
  }
  json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match) {
    let cls = 'number'
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        cls = 'key'
      } else {
        cls = 'string'
      }
    } else if (/true|false/.test(match)) {
      cls = 'boolean'
    } else if (/null/.test(match)) {
      cls = 'null'
    }
    return '<span class="' + cls + '">' + match + '</span>'
  })
}

export function getDynamicRange (period, selectedRange) {
  let till, from
  if ((!period || period === 'custom') && selectedRange) {
    return selectedRange
  }

  till = moment(new Date()).subtract(1, 'day').format('YYYY-MM-DD')
  switch (period) {
    case 'past-week':
      from = moment(new Date()).subtract(7, 'days').format('YYYY-MM-DD')
      break
    case 'past-month':
      from = moment(new Date()).subtract(31, 'days').format('YYYY-MM-DD')
      break
  }

  return { till, from }
}
